import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import {
    ClientSubscriptionService,
    ClientSubscriptionType,
    ClientSubscriptionDetail,
    ClientSubscriptionDetailForm,
} from '../../services/client-subscription.service';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { ToastService } from '../../../shared/services/toast.service';
import { filterBy } from '@progress/kendo-data-query';
import { MatTabChangeEvent } from '@angular/material/tabs';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'app-lba-subscriptions',
    templateUrl: './lba-subscriptions.component.html',
    styleUrls: ['./lba-subscriptions.component.scss'],
})
export class LbaSubscriptionsComponent implements OnInit {
    subscriptionType: ClientSubscriptionType[];
    subscriptionPages: any[];
    clients: ClientSubscriptionDetail[];
    originalClients: ClientSubscriptionDetail[];
    search: string;

    gridView: GridDataResult;
    gridViewClients: GridDataResult;
    public updateData: ClientSubscriptionDetailForm[];

    tabs = [{ label: 'Enrollments' }, { label: 'Client Assignment' }];

    tab: number = 0;

    pageSizes = true;
    previousNext = true;
    pageSize = 10;
    skip = 0;
    info = true;

    errors: string = null;
    addMenuOpen: boolean = false;
    editMenuOpen: boolean = false;
    newSubscriptionModel = null;
    editSubscriptionModel = null;

    /* Subscription Dropdown */
    selectedSubscription: ClientSubscriptionType[];
    subscriptionListSettings: any;
    cloneFlag: boolean;
    hasName: boolean = false;

    submitting: boolean = false;

    constructor(
        private clientSubscriptionService: ClientSubscriptionService,
        private toast: ToastService
    ) {}

    ngOnInit() {
        this.cloneFlag = false;
        this.getTypes();
        // -- Settings for Subscription dropdown
        this.subscriptionListSettings = {
            singleSelection: true,
            text: 'Select Enrollment',
            enableSearchFilter: false,
            showCheckbox: false,
            labelKey: 'name',
            primaryKey: 'subscriptionIdentifier',
            enableFilterSelectAll: false,
        };
    }

    getTypes() {
        this.clientSubscriptionService.getSubscriptionTypes().subscribe((data) => {
            this.subscriptionType = data;
            this.gridView = {
                data: this.subscriptionType,
                total: this.subscriptionType.length,
            };
        });
    }

    getClients() {
        this.clientSubscriptionService.getClientSubscriptionDetail().subscribe((data) => {
            this.clients = data;
            this.originalClients = JSON.parse(JSON.stringify(data));
            this.loadClientsIntoGridView();
        });
    }

    filterSearch() {
        if (this.search === '') {
            this.loadClientsIntoGridView(true);
        } else {
            let filteredClients = filterBy(this.clients, {
                logic: 'or',
                filters: [
                    {
                        field: 'clientFullName',
                        operator: 'contains',
                        value: this.search,
                        ignoreCase: true,
                    },
                ],
            });

            this.gridViewClients = {
                data: filteredClients.slice(this.skip, this.skip + this.pageSize),
                total: filteredClients.length,
            };
        }
    }

    getPages(key: number) {
        this.clientSubscriptionService.getSubscriptionPage(key).subscribe((data) => {
            this.subscriptionPages = data;
        });
    }

    addSubscription() {
        let subName: string = this.newSubscriptionModel.name;
        let subDesc: string = this.newSubscriptionModel.description;
        let cloneKey: number = this.cloneFlag
            ? this.selectedSubscription[0].clientSubscriptionTypeKey
            : null;

        if (this.subscriptionType.some((e) => e.name == subName) == false) {
            this.hasName = false;
            this.submitting = true;
            this.clientSubscriptionService.createType(subName, subDesc).subscribe(
                (data) => {
                    this.submitting = false;
                    let clientSubscriptionTypeKey: number = data;
                    if (this.cloneFlag && this.selectedSubscription) {
                        this.cloneSubscription(clientSubscriptionTypeKey, cloneKey); // Clone all existing pages

                        this.toggleAddMenu();
                        this.getTypes();
                    } else {
                        this.toast.success('Enrollment type has been created.');
                        this.toggleAddMenu();
                        this.getTypes();
                    }
                    this.cloneFlag = false;
                },
                (error) => {
                    this.submitting = false;
                    this.cloneFlag = false;
                    this.toast.warning('Something went wrong creating new enrollment type.');
                }
            );
        } else {
            this.hasName = true;
        }
    }

    cloneSubscription(clientSubscriptionTypeKey: number, cloneKey: number) {
        this.clientSubscriptionService
            .cloneSubscription(clientSubscriptionTypeKey, cloneKey)
            .subscribe(
                (data) => {
                    this.toast.success('Enrollment type has been cloned.');
                },
                (error) => {
                    this.toast.warning('Something went wrong cloning enrollment pages.');
                    throw new Error(error);
                }
            );
    }

    /* Clone Multiselection */
    onItemSelect(event) {
        if (this.cloneFlag) {
            let count: number = 2;
            this.newSubscriptionModel.name = this.selectedSubscription[0].name + ' - (Clone)';

            while (
                this.subscriptionType.find((type) => type.name == this.newSubscriptionModel.name)
            ) {
                this.newSubscriptionModel.name =
                    this.selectedSubscription[0].name + ' - (Clone ' + count.toString() + ')';
                count += 1;
            }

            this.newSubscriptionModel.description = this.selectedSubscription[0].description;
        }
    }

    editSubscription() {
        this.clientSubscriptionService
            .editType(
                this.editSubscriptionModel.clientSubscriptionTypeKey,
                this.editSubscriptionModel.name,
                this.editSubscriptionModel.description,
                this.editSubscriptionModel.isActive
            )
            .subscribe(
                (data) => {
                    this.toast.success('Enrollment type has been updated.');
                    this.toggleEditMenu();
                    this.getTypes();
                },
                (error) => {
                    this.toast.warning('Something went wrong updating enrollment type.');
                    this.toggleEditMenu();
                }
            );
    }

    setNameField() {
        if (this.subscriptionType.some((type) => type.name == this.newSubscriptionModel.name)) {
            this.hasName = true;
        } else {
            this.hasName = false;
        }
    }

    logTab(event: MatTabChangeEvent): void {
        this.tab = event.index;

        if (this.tab === 1 && !this.gridViewClients) this.getClients();
    }

    clientPageChange({ skip, take }: PageChangeEvent): void {
        this.skip = skip;
        this.pageSize = take;
        this.loadClientsIntoGridView();
    }

    loadClientsIntoGridView(showAll: boolean = false) {
        if (showAll) {
            this.skip = 0;
        }

        let data = this.clients;

        this.gridViewClients = {
            data: data.slice(this.skip, this.skip + this.pageSize),
            total: data.length,
        };
    }

    updateSubscription(sub: ClientSubscriptionDetail) {
        if (!this.updateData) {
            this.updateData = [];
        }

        let value = sub.clientSubscriptionTypeKey;

        let item = this.originalClients.find((s) => s.clientKey === sub.clientKey);
        if (item.clientSubscriptionTypeKey === value) {
            let update = this.updateData.find((s) => s.clientKey === sub.clientKey);
            let itemIndex = this.updateData.indexOf(update);
            if (itemIndex >= 0) {
                this.updateData.splice(itemIndex, 1);
            }
        } else {
            let update = this.updateData.find((s) => s.clientKey === sub.clientKey);
            if (update) {
                update = {
                    clientKey: sub.clientKey,
                    clientSubscriptionTypeKey: value,
                    clientSubscriptionKey: sub.clientSubscriptionKey
                        ? sub.clientSubscriptionKey
                        : null,
                };
            } else {
                this.updateData.push({
                    clientKey: sub.clientKey,
                    clientSubscriptionTypeKey: value,
                    clientSubscriptionKey: sub.clientSubscriptionKey
                        ? sub.clientSubscriptionKey
                        : null,
                });
            }
        }
    }

    update() {
        this.clientSubscriptionService
            .updateClientSubscription(this.updateData)
            .subscribe((data) => {
                this.toast.success('Client enrollment assignment updated.');
                this.updateData = [];
            });
    }

    // Add Menu
    toggleAddMenu() {
        this.hasName = false;
        this.cloneFlag = false;
        this.newSubscriptionModel = {
            name: null,
            description: null,
        };

        this.addMenuOpen = !this.addMenuOpen;
    }

    toggleEditMenu(data?) {
        this.editMenuOpen = !this.editMenuOpen;

        if (this.editMenuOpen === false) {
            this.editSubscriptionModel = {
                clientSubscriptionTypeKey: null,
                name: null,
                description: null,
                isActive: true,
            };
        } else {
            this.editSubscriptionModel = {
                clientSubscriptionTypeKey: data.clientSubscriptionTypeKey,
                name: data.name,
                description: data.description,
                isActive: data.isActive,
            };
        }
    }
}
